import React, { useEffect, useState } from "react";
import { Copy, Check, Download, Terminal, RefreshCw, Server, HardDrive, Cpu, Zap, Wifi, Package } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function CodeBox({ children, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Copiato negli appunti");
    } catch (e) {
      toast.error("Impossibile copiare");
    }
  };
  return (
    <div className="relative" data-testid={testid}>
      <pre className="code-block">{children}</pre>
      <button
        onClick={copy}
        data-testid={`${testid}-copy`}
        className="absolute top-2 right-2 p-1.5 bg-[#18181B] border border-[#27272A] hover:border-[#10B981] hover:text-[#10B981] rounded-sm text-[#A1A1AA]"
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

export default function AgentInstallPage() {
  const [settings, setSettings] = useState(null);
  const [rotating, setRotating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
  };
  useEffect(() => { load(); }, []);

  const rotate = async () => {
    if (!confirm("Rigenerare il token? Tutti i rig esistenti continueranno a funzionare, ma i prossimi installer dovranno usare il nuovo token.")) return;
    setRotating(true);
    try {
      await api.post("/settings/agent-token/rotate");
      toast.success("Nuovo token generato");
      load();
    } finally {
      setRotating(false);
    }
  };

  // In produzione il bundle è buildato con REACT_APP_BACKEND_URL='' (API relative):
  // per i comandi copia-incolla serve l'URL assoluto → fallback su window.location.origin
  const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const token = settings?.agent_api_token || "<TOKEN>";

  const oneLiner = `curl -fsSL ${backend}/install_agent.sh -o install_agent.sh && sudo API_URL='${backend}' AGENT_TOKEN='${token}' bash install_agent.sh`;
  const manualEnv = `API_URL=${backend}
AGENT_TOKEN=${token}`;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-5xl">
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#10B981] mb-1.5">
          // Rig Agent Setup
        </div>
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
          Agent Install
        </h1>
        <p className="text-sm text-[#A1A1AA] mt-1 max-w-2xl">
          Installa l'agent WartHive sui tuoi 15 PC Ubuntu Server. L'agent
          gestisce <span className="text-[#FAFAFA]">bzminer</span>, raccoglie le
          metriche NVIDIA e applica overclock / comandi remoti.
        </p>
      </div>

      {/* Token panel */}
      <div className="bento p-5 mb-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
              Token API condiviso
            </div>
            <h3 className="font-heading font-bold text-lg mt-1">Registration Token</h3>
            <p className="text-xs text-[#A1A1AA] mt-1 max-w-xl">
              Necessario solo al primo avvio dell'agent. Una volta registrato,
              ogni rig riceve un token personale.
            </p>
          </div>
          <button
            onClick={rotate}
            disabled={rotating}
            data-testid="agent-rotate-btn"
            className="flex items-center gap-1.5 bg-[#27272A] hover:bg-[#3F3F46] text-[#FAFAFA] border border-[#3F3F46] px-3 py-2 rounded-sm text-xs font-mono uppercase tracking-wider"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${rotating ? "animate-spin" : ""}`} /> Rotate
          </button>
        </div>
        <CodeBox testid="agent-token-box">{token}</CodeBox>
      </div>

      {/* Quick install */}
      <div className="bento p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Terminal className="w-4 h-4 text-[#10B981]" />
          <h3 className="font-heading font-bold text-lg">Install rapido (one-liner)</h3>
        </div>
        <p className="text-xs text-[#A1A1AA] mb-3">
          Esegui questo comando sul rig Ubuntu Server come root. Lo script
          installa nvidia-utils, scarica bzminer, configura il servizio
          systemd <span className="text-[#FAFAFA] font-mono">warthive-agent</span>{" "}
          e collega il rig alla dashboard.{" "}
          <span className="text-[#10B981]">Non tocca MAI la rete: LAN/WiFi
          esistenti restano intatti</span> (niente driver di rete, niente
          network-manager).
        </p>
        <CodeBox testid="agent-oneliner-box">{oneLiner}</CodeBox>
        <div className="mt-3 text-[11px] font-mono text-[#A1A1AA]">
          ⚠ Se mancano i driver NVIDIA, lo script installa SOLO il driver NVIDIA
          consigliato. In alternativa manuale:
          <span className="text-[#FAFAFA]"> sudo apt install nvidia-driver-550-server &amp;&amp; sudo reboot</span>
        </div>
      </div>

      {/* Manual install */}
      <div className="bento p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Download className="w-4 h-4 text-[#10B981]" />
          <h3 className="font-heading font-bold text-lg">Install manuale (step-by-step)</h3>
        </div>

        <ol className="space-y-4 text-sm">
          <li>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#10B981] mb-1">
              1 · Prerequisiti
            </div>
            <CodeBox testid="agent-step1">{`sudo apt update && sudo apt install -y python3 python3-pip python3-venv curl wget unzip lsb-release ca-certificates
# Driver NVIDIA (se mancano) — SOLO il driver NVIDIA, NON 'autoinstall' (toccherebbe la rete):
sudo apt install -y nvidia-driver-550-server && sudo reboot`}</CodeBox>
          </li>

          <li>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#10B981] mb-1">
              2 · Scarica lo script di installazione
            </div>
            <CodeBox testid="agent-step2">{`curl -fsSL ${backend}/install_agent.sh -o install_agent.sh
chmod +x install_agent.sh`}</CodeBox>
          </li>

          <li>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#10B981] mb-1">
              3 · Configura env e installa
            </div>
            <CodeBox testid="agent-step3">{`sudo API_URL='${backend}' AGENT_TOKEN='${token}' ./install_agent.sh`}</CodeBox>
          </li>

          <li>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#10B981] mb-1">
              4 · Verifica
            </div>
            <CodeBox testid="agent-step4">{`sudo systemctl status warthive-agent
sudo journalctl -u warthive-agent -f`}</CodeBox>
          </li>
        </ol>
      </div>

      {/* Clone procedure */}
      <div className="bento p-5 mb-6 border-l-2 border-[#F59E0B]/50">
        <div className="flex items-center gap-2 mb-3">
          <Terminal className="w-4 h-4 text-[#F59E0B]" />
          <h3 className="font-heading font-bold text-lg">Clonare l'immagine sui 14 rig rimanenti</h3>
        </div>
        <p className="text-xs text-[#A1A1AA] mb-4">
          Procedura <span className="text-[#10B981]">"smart fisica"</span>: cloni l'SSD di rig01 con un adattatore USB-SATA{" "}
          <span className="text-[#FAFAFA]">senza toccare rig01</span>. Su ogni clone esegui <span className="text-[#FAFAFA] font-mono">init_clone.sh</span>
          {" "}che cancella l'identità duplicata e imposta il nuovo hostname.
        </p>

        <ol className="space-y-4 text-sm">
          <li>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] mb-1">
              1 · Sul RIG MASTER (rig01) — opzionale slim
            </div>
            <p className="text-xs text-[#A1A1AA] mb-2">
              Riduci la dimensione dell'immagine rimuovendo bloatware (snap, cloud-init, doc, ecc.). Non tocca NVIDIA / mining stack.
            </p>
            <CodeBox testid="clone-step-slim">{`curl -fsSL ${backend}/slim_for_clone.sh -o slim_for_clone.sh
chmod +x slim_for_clone.sh
sudo ./slim_for_clone.sh --status   # cosa rimuoverebbe (no-op)
sudo ./slim_for_clone.sh --apply    # esegue la pulizia`}</CodeBox>
          </li>

          <li>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] mb-1">
              2 · Spegni rig01 e clona il disco
            </div>
            <p className="text-xs text-[#A1A1AA] mb-2">
              Spegni la macchina, stacca l'SSD, collegalo a un adattatore USB-SATA su un PC con <span className="text-[#FAFAFA] font-mono">Clonezilla</span>. Crea un'immagine OPPURE clona disco→disco. Poi rimetti l'SSD originale in rig01 (<span className="text-[#10B981]">rig01 resta intatto al 100%</span>) e installa i dischi clonati nei nuovi rig.
            </p>
            <CodeBox testid="clone-step-shutdown">{`# Su rig01:
sudo shutdown -h now

# Poi: stacca SSD → adattatore USB-SATA → clona con Clonezilla → reinstalla nei 14 rig`}</CodeBox>
          </li>

          <li>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] mb-1">
              3 · Su OGNI clone (rig02 ... rig15) — PRIMA di collegare la rete
            </div>
            <p className="text-xs text-[#A1A1AA] mb-2">
              ⚠ <span className="text-[#FAFAFA]">Lascia l'Ethernet staccato</span> al primo boot del clone, così l'agent non manda heartbeat col vecchio token di rig01. Esegui <span className="text-[#FAFAFA] font-mono">init_clone.sh</span> (scaricalo via USB o collega la rete temporaneamente solo per il download). Lo script cancella state.json, rigenera machine-id + chiavi SSH, e imposta il nuovo hostname.
            </p>
            <CodeBox testid="clone-step-init">{`# Su rig02 (Ethernet ANCORA staccato o disabilitato):
curl -fsSL ${backend}/init_clone.sh -o init_clone.sh
chmod +x init_clone.sh
sudo ./init_clone.sh rig02      # ← cambia il nome per ogni rig (rig03, rig04, ...)

# Adesso collega Ethernet + avvia agent:
sudo systemctl start warthive-agent`}</CodeBox>
            <p className="text-xs text-[#A1A1AA] mt-2">
              Il rig comparirà nella dashboard come <span className="text-[#FAFAFA] font-mono">rig02</span> entro 5 secondi.
            </p>
          </li>
        </ol>
      </div>

      {/* Env reference */}
      <div className="bento p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Server className="w-4 h-4 text-[#10B981]" />
          <h3 className="font-heading font-bold text-lg">Variabili d'ambiente</h3>
        </div>
        <p className="text-xs text-[#A1A1AA] mb-3">
          Vengono scritte in <span className="text-[#FAFAFA] font-mono">/etc/warthive/agent.env</span> dallo script.
        </p>
        <CodeBox testid="agent-env-box">{manualEnv}</CodeBox>
      </div>

      {/* Network drivers only (no internet on rig) */}
      <div className="bento p-5 mb-6 border-l-2 border-[#10B981]/40" data-testid="netdebs-section">
        <div className="flex items-center gap-2 mb-3">
          <Wifi className="w-4 h-4 text-[#10B981]" />
          <h3 className="font-heading font-bold text-lg">Solo driver di rete (rig senza LAN/WiFi)</h3>
        </div>
        <p className="text-xs text-[#A1A1AA] mb-3 max-w-2xl">
          Caso classico: il rig ha Ubuntu installato ma <span className="text-[#FAFAFA]">non vede la rete</span> (LAN/WiFi non funzionano). Scarichi i driver su USB da un PC con internet, poi li installi sul rig.
        </p>

        <div className="space-y-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#10B981] mb-1">
              1 · Da PC CON internet (anche Windows/WSL o un altro rig)
            </div>
            <CodeBox testid="netdebs-download-box">{`curl -fsSL ${backend}/download_network_debs.sh -o download_network_debs.sh
chmod +x download_network_debs.sh
./download_network_debs.sh                       # output in ./network-debs (~400 MB)
# Copia la cartella su USB:
cp -r network-debs /media/USB/`}</CodeBox>
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#10B981] mb-1">
              2 · Sul rig senza rete
            </div>
            <CodeBox testid="netdebs-install-box">{`# Inserisci USB e monta (sostituisci /dev/sdb1 con il device della tua USB)
sudo mkdir -p /mnt/usb
sudo mount /dev/sdb1 /mnt/usb

# Installa i driver
cd /mnt/usb/network-debs
sudo ./install_network.sh

# Configura WiFi se serve
sudo nmtui                                # TUI interattiva
# oppure
sudo nmcli dev wifi connect "MioSSID" password "miapassword"`}</CodeBox>
          </div>
        </div>

        <p className="text-xs text-[#A1A1AA] mt-3">
          Include: <span className="text-[#FAFAFA]">linux-firmware</span>, <span className="text-[#FAFAFA]">network-manager</span>, <span className="text-[#FAFAFA]">wpasupplicant</span>, <span className="text-[#FAFAFA]">r8168-dkms + r8125-dkms</span> (Realtek 1G/2.5G), <span className="text-[#FAFAFA]">wireless-regdb</span>, kernel headers, microcodice CPU. Tutte le dipendenze incluse.
        </p>
      </div>

      {/* Offline install bundle */}
      <div className="bento p-5 mb-6 border-l-2 border-[#A1A1AA]/40" data-testid="offline-section">
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-4 h-4 text-[#FAFAFA]" />
          <h3 className="font-heading font-bold text-lg">Installazione offline (rig senza internet)</h3>
        </div>
        <p className="text-xs text-[#A1A1AA] mb-3 max-w-2xl">
          Per i rig che <span className="text-[#FAFAFA]">non hanno connessione</span>: genera un bundle portatile (.tar.gz da ~200 MB) su una macchina <b>CON</b> internet (es. rig01), poi copialo via USB sui rig offline.
        </p>

        <div className="space-y-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1">
              1 · Genera il bundle (su un rig CON internet)
            </div>
            <CodeBox testid="offline-build-box">{`curl -fsSL ${backend}/build_offline_bundle.sh -o build_offline_bundle.sh
chmod +x build_offline_bundle.sh
sudo BACKEND_URL='${backend}' \\
     AGENT_TOKEN='${token}' \\
     ./build_offline_bundle.sh
# → produce warthive-offline-YYYYMMDD.tar.gz nella dir corrente`}</CodeBox>
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1">
              2 · Sul rig offline (nessun internet richiesto)
            </div>
            <CodeBox testid="offline-install-box">{`# Copia warthive-offline-*.tar.gz via USB nel rig, poi:
tar xzf warthive-offline-*.tar.gz
cd warthive-offline-*
sudo ./install_offline.sh                  # installa tutto da bundle
# Se il rig non avrà MAI internet: sudo SKIP_NETWORK=1 ./install_offline.sh
sudo reboot`}</CodeBox>
          </div>
        </div>

        <p className="text-xs text-[#A1A1AA] mt-3">
          Il bundle include: <span className="text-[#FAFAFA]">agent.py</span>, <span className="text-[#FAFAFA]">bzminer</span>, tutti i pacchetti <span className="text-[#FAFAFA]">.deb</span> base (python, nvtop, network-manager…), i wheel Python (<span className="text-[#FAFAFA]">httpx, websockets</span>), e gli script ausiliari. <span className="text-[#F59E0B]">Driver NVIDIA esclusi</span> (troppo pesanti — installali a parte con <span className="text-[#FAFAFA] font-mono">apt</span>).
        </p>
      </div>

      {/* WiFi setup helper */}
      <div className="bento p-5 mb-6 border-l-2 border-[#3B82F6]/50" data-testid="wifi-section">
        <div className="flex items-center gap-2 mb-3">
          <Wifi className="w-4 h-4 text-[#3B82F6]" />
          <h3 className="font-heading font-bold text-lg">Configurare WiFi (se il rig non ha cavo Ethernet)</h3>
        </div>
        <p className="text-xs text-[#A1A1AA] mb-3 max-w-2xl">
          L'<span className="text-[#FAFAFA] font-mono">install_agent.sh</span> installa già <span className="text-[#FAFAFA] font-mono">network-manager</span> + firmware necessari, e auto-rileva i driver Realtek <span className="text-[#FAFAFA] font-mono">RTL8125</span> (2.5G) e <span className="text-[#FAFAFA] font-mono">RTL8168</span> (1G). Per <b>WiFi</b> usa lo script seguente:
        </p>
        <CodeBox testid="wifi-script-box">{`curl -fsSL ${backend}/wifi_connect.sh -o wifi_connect.sh
chmod +x wifi_connect.sh
sudo ./wifi_connect.sh                          # interattivo (mostra reti)
sudo ./wifi_connect.sh "MioSSID" "miapassword"  # diretto
sudo ./wifi_connect.sh --list                   # mostra reti visibili
sudo ./wifi_connect.sh --status                 # stato connessione`}</CodeBox>
        <p className="text-xs text-[#A1A1AA] mt-3">
          Alternativa: <span className="text-[#FAFAFA] font-mono">sudo nmtui</span> (TUI grafica, inclusa in network-manager).
          La connessione viene salvata e si riconnette automaticamente al boot.
        </p>
      </div>

      {/* Hardening RAM-disk */}
      <div className="bento p-5 border-l-2 border-[#3B82F6]/50">
        <div className="flex items-center gap-2 mb-3">
          <HardDrive className="w-4 h-4 text-[#3B82F6]" />
          <h3 className="font-heading font-bold text-lg">Hardening RAM-disk (opzionale, consigliato)</h3>
        </div>
        <p className="text-xs text-[#A1A1AA] mb-3 max-w-2xl">
          Sposta <span className="text-[#FAFAFA] font-mono">/tmp</span>,{" "}
          <span className="text-[#FAFAFA] font-mono">/var/log</span>,{" "}
          <span className="text-[#FAFAFA] font-mono">/var/tmp</span> e{" "}
          <span className="text-[#FAFAFA] font-mono">/var/cache</span> su <b>RAM-disk (tmpfs)</b> — come fa Hive OS.
          Riduce l'usura di SSD/NVMe/USB, accelera i log del miner e mantiene il sistema "stateless".
          Lo stato dell'agent in <span className="text-[#FAFAFA] font-mono">/etc/warthive/</span> rimane su disco
          (il rig mantiene la sua identità tra reboot).
        </p>
        <div className="mb-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs font-mono">
          <div className="bg-[#0B0B0F] border border-[#27272A] rounded-sm p-3">
            <div className="text-[#3B82F6] text-[10px] uppercase tracking-[0.15em] mb-1">SU DISCO</div>
            <div className="text-[#FAFAFA]">/ (root)</div>
            <div className="text-[#FAFAFA]">/etc/warthive/state.json</div>
            <div className="text-[#A1A1AA] text-[10px] mt-1">noatime, no auto-update</div>
          </div>
          <div className="bg-[#0B0B0F] border border-[#27272A] rounded-sm p-3">
            <div className="text-[#10B981] text-[10px] uppercase tracking-[0.15em] mb-1">SU RAM (max 50%)</div>
            <div className="text-[#FAFAFA]">/tmp, /var/tmp</div>
            <div className="text-[#FAFAFA]">/var/log, /var/cache</div>
            <div className="text-[#A1A1AA] text-[10px] mt-1">8 GB RAM → ~4 GB tmpfs</div>
          </div>
          <div className="bg-[#0B0B0F] border border-[#27272A] rounded-sm p-3">
            <div className="text-[#F59E0B] text-[10px] uppercase tracking-[0.15em] mb-1">JOURNALD</div>
            <div className="text-[#FAFAFA]">volatile, 256MB max</div>
            <div className="text-[#A1A1AA] text-[10px] mt-1">log persi al reboot</div>
            <div className="text-[#A1A1AA] text-[10px]">(WartHive li raccoglie via telemetry)</div>
          </div>
        </div>
        <CodeBox testid="harden-box">{`curl -fsSL ${backend}/harden_for_usb.sh -o harden_for_usb.sh
chmod +x harden_for_usb.sh
sudo ./harden_for_usb.sh
sudo reboot`}</CodeBox>
        <div className="mt-3 text-[11px] font-mono text-[#A1A1AA]">
          Comandi utili: <span className="text-[#FAFAFA]">sudo ./harden_for_usb.sh --status</span> · <span className="text-[#FAFAFA]">sudo ./harden_for_usb.sh --revert</span> (ripristina lo stato originale).
        </div>
      </div>

      {/* Overclock NVIDIA — driver downgrade + Xorg headless */}
      <div className="bento p-5 mt-6 border-l-2 border-[#F59E0B]/50" data-testid="overclock-section">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-[#F59E0B]" />
          <h3 className="font-heading font-bold text-lg">Sbloccare Overclock reale (Offset + Fan)</h3>
        </div>
        <p className="text-xs text-[#A1A1AA] mb-4 max-w-3xl">
          Senza Xorg, su Ubuntu Server NVIDIA blocca offset clock e fan control.
          Sui driver <span className="text-[#FAFAFA] font-mono">590+</span> anche
          <span className="text-[#FAFAFA] font-mono"> nvidia-smi -ac</span> viene
          ignorato durante il mining (P-state forzato). La via maestra è:
          downgrade al ramo <span className="text-[#FAFAFA] font-mono">550-server LTS</span> + Xorg "fake" headless con
          <span className="text-[#FAFAFA] font-mono"> coolbits=28</span>.
        </p>

        <ol className="space-y-5 text-sm">
          <li>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] mb-2">
              <Cpu className="w-3 h-3" />
              1 · Downgrade driver NVIDIA → 550-server (LTS)
            </div>
            <p className="text-xs text-[#A1A1AA] mb-2">
              Purga i driver attuali e installa <span className="text-[#FAFAFA] font-mono">nvidia-driver-550-server</span> con apt-mark hold. Richiede <b className="text-[#FAFAFA]">reboot</b>.
            </p>
            <CodeBox testid="nvidia-downgrade-box">{`curl -fsSL ${backend}/nvidia_downgrade.sh -o nvidia_downgrade.sh
chmod +x nvidia_downgrade.sh
sudo ./nvidia_downgrade.sh
sudo reboot`}</CodeBox>
            <div className="mt-2 text-[11px] font-mono text-[#A1A1AA]">
              Altre versioni: <span className="text-[#FAFAFA]">sudo NVIDIA_TARGET=535-server ./nvidia_downgrade.sh</span> · skip prompt: <span className="text-[#FAFAFA]">sudo CONFIRM=1 ./nvidia_downgrade.sh</span>
            </div>
          </li>

          <li>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] mb-2">
              <Terminal className="w-3 h-3" />
              2 · Setup Xorg "fake" headless (coolbits=28)
            </div>
            <p className="text-xs text-[#A1A1AA] mb-2">
              Avvia un X server senza monitor in modo che <span className="text-[#FAFAFA] font-mono">nvidia-settings</span> possa applicare offset e fan speed. Crea il servizio <span className="text-[#FAFAFA] font-mono">warthive-xorg.service</span>.
            </p>
            <CodeBox testid="xorg-setup-box">{`curl -fsSL ${backend}/setup_xorg_headless.sh -o setup_xorg_headless.sh
chmod +x setup_xorg_headless.sh
sudo ./setup_xorg_headless.sh`}</CodeBox>
            <div className="mt-2 text-[11px] font-mono text-[#A1A1AA]">
              Verifica: <span className="text-[#FAFAFA]">DISPLAY=:0 XAUTHORITY=/root/.Xauthority nvidia-settings -q gpus</span>
            </div>
          </li>
        </ol>
      </div>
    </div>
  );
}
